import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Loader2 } from 'lucide-react';
import { useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';

import { Badge, Button, Card, CardContent, CardHeader, Textarea } from '@/components/ui';
import { errorsApi } from '@/lib/api';
import { cn } from '@/lib/cn';
import type { ErrorListItem, ResolutionAction } from '@/types/api';

import { HaltReviewLayout } from './layouts/HaltReviewLayout';
import { ConflictReviewLayout } from './layouts/ConflictReviewLayout';
import { StandardReviewLayout } from './layouts/StandardReviewLayout';

/**
 * Spec S08 — Error Queue detail. The page routes to one of three layouts
 * depending on the error type.
 */
export function ErrorDetail() {
  const { errorId } = useParams();
  const { data, isLoading, error } = useQuery({
    queryKey: ['studio', 'errors', errorId],
    queryFn: () => errorsApi.get(errorId as string),
    enabled: Boolean(errorId),
  });

  if (isLoading) {
    return (
      <div className="flex h-full items-center justify-center">
        <Loader2 className="h-5 w-5 animate-spin text-slate-400" aria-hidden />
      </div>
    );
  }
  if (error || !data) {
    return (
      <div className="p-6">
        <p className="text-body text-red-700">Failed to load this error.</p>
        <Button asChild variant="ghost" className="mt-2">
          <Link to="/studio/errors">
            <ArrowLeft className="mr-1 h-4 w-4" aria-hidden /> Back
          </Link>
        </Button>
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col">
      <header className="flex items-center gap-3 border-b border-slate-200 bg-white px-6 py-3">
        <Button asChild variant="ghost" size="sm">
          <Link to="/studio/errors">
            <ArrowLeft className="mr-1 h-4 w-4" aria-hidden /> Back
          </Link>
        </Button>
        <div className="flex items-center gap-2">
          <Badge variant="error">{data.error_type}</Badge>
          {data.error_subtype && (
            <span className="text-small text-slate-600">{data.error_subtype}</span>
          )}
          {data.severity && (
            <Badge
              variant={
                data.severity === 'BLOCKER' || data.severity === 'HIGH'
                  ? 'error'
                  : data.severity === 'MEDIUM'
                    ? 'warning'
                    : 'neutral'
              }
            >
              {data.severity}
            </Badge>
          )}
        </div>
        <span className="ml-auto font-mono text-small text-slate-500">
          {data.error_id.slice(0, 8)}
        </span>
      </header>

      <div className="flex-1 overflow-y-auto">
        {data.error_type === 'H' ? (
          <HaltReviewLayout error={data} />
        ) : data.error_type === 'D' ? (
          <ConflictReviewLayout error={data} />
        ) : (
          <StandardReviewLayout error={data} />
        )}
      </div>
    </div>
  );
}

// ─── Reusable resolution panel ──────────────────────────────────

interface ResolutionFormProps {
  error: ErrorListItem;
  availableActions: ResolutionAction[];
  decisionTypes: Record<ResolutionAction, string[]>;
}

export function ResolutionPanel({
  error,
  availableActions,
  decisionTypes,
}: ResolutionFormProps) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const recommended = error.guidance?.recommended_action ?? null;
  // Honour the playbook's recommendation when it's one of the buttons
  // this layout actually exposes — otherwise fall back to the first.
  const defaultAction: ResolutionAction =
    recommended && availableActions.includes(recommended)
      ? recommended
      : (availableActions[0] ?? 'fix_and_accept');
  const [action, setAction] = useState<ResolutionAction>(defaultAction);
  const [decisionType, setDecisionType] = useState<string>(decisionTypes[action]?.[0] ?? '');
  const [note, setNote] = useState('');
  const [correctedValue, setCorrectedValue] = useState('');
  const [trainingSignal, setTrainingSignal] = useState(true);
  const [applyToGroup, setApplyToGroup] = useState(false);

  // How many OTHER open errors share this row's signature? Surfaced so
  // the analyst sees the blast radius before opting into a bulk resolve.
  const { data: identical } = useQuery({
    queryKey: ['studio', 'errors', error.error_id, 'identical-count'],
    queryFn: () => errorsApi.identicalCount(error.error_id),
    staleTime: 30_000,
  });
  const identicalCount = identical?.count ?? 0;

  const resolve = useMutation({
    mutationFn: () =>
      errorsApi.resolve(error.error_id, {
        action,
        decision_type: decisionType || action,
        corrected_value: correctedValue || undefined,
        note: note || undefined,
        log_as_training_signal: trainingSignal,
        apply_to_group: applyToGroup && identicalCount > 0,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['studio', 'errors'] });
      navigate('/studio/errors');
    },
  });

  return (
    <div className="space-y-3">
      <div>
        <p className="mb-1 text-h4 text-slate-700">Action</p>
        <div className="flex flex-wrap gap-1">
          {availableActions.map((a) => {
            const isRecommended = a === recommended;
            return (
              <button
                key={a}
                type="button"
                onClick={() => {
                  setAction(a);
                  setDecisionType(decisionTypes[a]?.[0] ?? a);
                }}
                className={cn(
                  'inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-small',
                  action === a
                    ? 'bg-accent text-white'
                    : 'bg-slate-100 text-slate-700 hover:bg-slate-200',
                  isRecommended && action !== a && 'ring-1 ring-accent/40',
                )}
                title={isRecommended ? 'Playbook recommendation' : undefined}
              >
                <span>{a.replace(/_/g, ' ')}</span>
                {isRecommended && (
                  <span
                    aria-hidden
                    className={cn(
                      'rounded px-1 text-[10px] font-semibold uppercase tracking-wide',
                      action === a
                        ? 'bg-white/20 text-white'
                        : 'bg-accent/15 text-accent-700',
                    )}
                  >
                    rec.
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {decisionTypes[action] && decisionTypes[action].length > 1 && (
        <div>
          <p className="mb-1 text-h4 text-slate-700">Decision type</p>
          <select
            value={decisionType}
            onChange={(e) => setDecisionType(e.target.value)}
            className="h-8 w-full rounded border border-slate-300 px-2 text-small"
          >
            {decisionTypes[action].map((t) => (
              <option key={t} value={t}>
                {t}
              </option>
            ))}
          </select>
        </div>
      )}

      {action === 'fix_and_accept' && (
        <div>
          <p className="mb-1 text-h4 text-slate-700">Corrected value</p>
          <Textarea
            rows={2}
            value={correctedValue}
            onChange={(e) => setCorrectedValue(e.target.value)}
            placeholder="Replacement value (optional)"
          />
        </div>
      )}

      <div>
        <p className="mb-1 text-h4 text-slate-700">Analyst note</p>
        <Textarea
          rows={3}
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="Optional rationale (training signal context)"
        />
      </div>

      <label className="flex items-center gap-2 text-small text-slate-600">
        <input
          type="checkbox"
          checked={trainingSignal}
          onChange={(e) => setTrainingSignal(e.target.checked)}
        />
        Log to Training Signal Store
      </label>

      {identicalCount > 0 && (
        <label
          className="flex items-start gap-2 rounded border border-amber-200 bg-amber-50/60 px-2 py-1.5 text-small text-amber-900"
          title="Same Source text snippet AND same error type"
        >
          <input
            type="checkbox"
            checked={applyToGroup}
            onChange={(e) => setApplyToGroup(e.target.checked)}
            className="mt-0.5"
          />
          <span>
            Apply to <strong>{identicalCount}</strong>{' '}
            {identicalCount === 1 ? 'identical error' : 'identical errors'} too
            <br />
            <span className="text-amber-800/80">
              Resolves every open row of type <strong>{error.error_type}</strong>{' '}
              from this document whose Source text matches.
            </span>
          </span>
        </label>
      )}

      <Button onClick={() => resolve.mutate()} disabled={resolve.isPending} className="w-full">
        {resolve.isPending
          ? 'Submitting…'
          : applyToGroup && identicalCount > 0
            ? `Submit (${identicalCount + 1} rows)`
            : 'Submit resolution'}
      </Button>
    </div>
  );
}
